import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX, 
  MoreVertical,
  Search,
  Filter
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AdminUserTable = ({ users, pagination, onSearch, onFilter, filters }) => {
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Mutations
  const updateUserSubscriptionMutation = useMutation(
    ({ userId, subscriptionType }) => 
      adminAPI.updateUserSubscription(userId, { subscriptionType }),
    {
      onSuccess: () => {
        toast.success('User subscription updated successfully');
        queryClient.invalidateQueries('adminUsers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update subscription');
      }
    }
  );

  const updateUserStatusMutation = useMutation(
    ({ userId, isActive }) => adminAPI.updateUserStatus(userId, { isActive }),
    {
      onSuccess: () => {
        toast.success('User status updated successfully');
        queryClient.invalidateQueries('adminUsers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update user status');
      }
    }
  );

  const handleSubscriptionChange = (userId, currentType) => {
    const newType = currentType === 'free' ? 'premium' : 'free';
    updateUserSubscriptionMutation.mutate({ userId, subscriptionType: newType });
  };

  const handleStatusToggle = (userId, isActive) => {
    updateUserStatusMutation.mutate({ userId, isActive: !isActive });
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  };

  return (
    <div className="card">
      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-discord-light" />
              <input
                type="text"
                placeholder="Search users..."
                value={filters.search || ''}
                onChange={(e) => onSearch(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          
          <select
            value={filters.subscription || ''}
            onChange={(e) => onFilter('subscription', e.target.value)}
            className="input w-auto"
          >
            <option value="">All Subscriptions</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
            <option value="pro">Pro</option>
            <option value="server">Server</option>
          </select>

          <select
            value={filters.sortBy || 'createdAt'}
            onChange={(e) => onFilter('sortBy', e.target.value)}
            className="input w-auto"
          >
            <option value="createdAt">Created Date</option>
            <option value="username">Username</option>
            <option value="gameStats.totalGamesPlayed">Games Played</option>
            <option value="gameStats.winRate">Win Rate</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="mb-4 p-3 bg-discord-blurple/20 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-discord-lightest">
              {selectedUsers.length} user(s) selected
            </span>
            <div className="flex space-x-2">
              <button className="btn btn-sm btn-secondary">
                Bulk Suspend
              </button>
              <button className="btn btn-sm btn-secondary">
                Bulk Activate
              </button>
              <button className="btn btn-sm btn-secondary">
                Change Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-discord-dark">
              <th className="text-left py-3 px-4">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-discord-light">User</th>
              <th className="text-left py-3 px-4 text-discord-light">Subscription</th>
              <th className="text-left py-3 px-4 text-discord-light">Games</th>
              <th className="text-left py-3 px-4 text-discord-light">Win Rate</th>
              <th className="text-left py-3 px-4 text-discord-light">Joined</th>
              <th className="text-left py-3 px-4 text-discord-light">Status</th>
              <th className="text-left py-3 px-4 text-discord-light">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="border-b border-discord-dark/50">
                <td className="py-3 px-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => handleSelectUser(user._id)}
                    className="rounded"
                  />
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-3">
                    {user.avatar ? (
                      <img
                        src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                        alt={user.username}
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-discord-blurple flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-discord-lightest font-medium">
                        {user.username}
                      </p>
                      <p className="text-sm text-discord-light">
                        #{user.discriminator}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${
                    user.subscription.type === 'free' ? '' :
                    user.subscription.type === 'premium' ? 'badge-primary' :
                    user.subscription.type === 'pro' ? 'badge-success' :
                    'badge-warning'
                  }`}>
                    {user.subscription.type.charAt(0).toUpperCase() + user.subscription.type.slice(1)}
                  </span>
                </td>
                <td className="py-3 px-4 text-discord-lightest">
                  {user.gameStats?.totalGamesPlayed || 0}
                </td>
                <td className="py-3 px-4 text-discord-lightest">
                  {user.gameStats?.winRate || 0}%
                </td>
                <td className="py-3 px-4 text-discord-light">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${
                    user.isActive ? 'badge-success' : 'badge-danger'
                  }`}>
                    {user.isActive ? 'Active' : 'Suspended'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSubscriptionChange(user._id, user.subscription.type)}
                      className="p-1 text-discord-blurple hover:text-discord-blurple/80"
                      title="Change Subscription"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleStatusToggle(user._id, user.isActive)}
                      className={`p-1 ${
                        user.isActive 
                          ? 'text-discord-red hover:text-discord-red/80' 
                          : 'text-discord-green hover:text-discord-green/80'
                      }`}
                      title={user.isActive ? 'Suspend User' : 'Activate User'}
                    >
                      {user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-discord-light">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} users
          </p>
          
          <div className="flex space-x-2">
            <button
              disabled={pagination.page === 1}
              className="btn btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={pagination.page === pagination.pages}
              className="btn btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserTable;
